// src/pages/StatsPage.jsx
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { HiCheck, HiCube, HiPlay, HiExclamation } from 'react-icons/hi';
import { getRecommendedStrategies } from '../api/apiClient';
import { useNavigate } from 'react-router-dom';

const RiskBadge = ({ leverage, manualLevel }) => {
    const { t } = useTranslation();
    let level = manualLevel; 
    
    if (!level || level === '') {
        const lev = Number(leverage) || 0;
        if (lev > 15) level = 'high';
        else if (lev > 5) level = 'medium';
        else level = 'low';
    }

    const configs = {
        low: { color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20', text: t('stats.risk_low') },
        medium: { color: 'bg-amber-500/10 text-amber-400 border-amber-500/20', text: t('stats.risk_medium') },
        high: { color: 'bg-rose-500/10 text-rose-400 border-rose-500/20', text: t('stats.risk_high') }
    };

    const current = configs[level.toLowerCase()] || configs.low;

    return (
        <span className={`px-2 py-0.5 rounded border text-[10px] font-bold uppercase tracking-wider ${current.color}`}>
            {current.text}
        </span>
    );
};

const ParamBlock = ({ label, value, color = "text-white" }) => (
    <div className="bg-slate-900/40 border border-slate-700/30 p-2.5 rounded-xl flex flex-col justify-center">
        <span className="text-[9px] text-slate-500 uppercase font-black tracking-tight mb-0.5 truncate">{label}</span>
        <span className={`font-mono font-bold text-xs ${color} truncate`}>{value}</span>
    </div>
);

export const StrategyDisplayCard = ({ strategy, onAdd, isPreview = false }) => {
  const { t } = useTranslation();
  const p = strategy.parameters || {};

  const rawComm = parseFloat(String(p.commission_rate).replace(',', '.'));
  const displayComm = !isNaN(rawComm) ? `${(rawComm * 100).toFixed(2)}%` : '0.00%';

  return (
    <div className={`relative bg-slate-800 rounded-2xl overflow-hidden border border-slate-700/50 flex flex-col h-full transition-all duration-300 ${!isPreview ? 'hover:border-blue-500/50 hover:shadow-2xl hover:shadow-blue-500/10 group' : ''}`}>
      
      <div className="p-5 pb-2 relative z-10 flex justify-between items-start">
        <div className="flex items-center gap-3 overflow-hidden"> {/* Добавил overflow-hidden для контейнера */}
            <img src="/logo_logo.svg" alt="Logo" className="w-10 h-10 object-contain shrink-0" />
            <div className="min-w-0 flex flex-col justify-center">
                {/* ИСПРАВЛЕНИЕ: leading-none -> leading-snug и добавил py-0.5 */}
                <h3 className="text-lg font-black text-white leading-snug truncate py-0.5">
                    {strategy.customStrategyName || t('stats.default_name')}
                </h3>
                <span className="text-[9px] text-slate-500 font-bold uppercase tracking-tighter block -mt-0.5">{t('stats.by_hedger')}</span>
            </div>
        </div>
        <div className="shrink-0 ml-2"> {/* Обертка для бейджа чтобы он не сжимался */}
            <RiskBadge leverage={p.leverage} manualLevel={p.risk_level} />
        </div>
      </div>

      <div className="px-5 py-3">
        <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] font-mono text-blue-400 bg-blue-400/10 px-1.5 py-0.5 rounded uppercase font-bold border border-blue-400/20">
                {p.symbol_a || 'SYM'} / {p.symbol_b || 'SYM'}
            </span>
        </div>
        <p className="text-xs text-slate-400 leading-relaxed line-clamp-2 min-h-[32px] italic">
          {p.description || t('stats.default_desc')}
        </p>
      </div>

      <div className="px-5 grid grid-cols-3 gap-2 mt-2 mb-6">
        <ParamBlock label={t('strategy_settings.timeframe')} value={`${p.timeframe || 0}m`} />
        <ParamBlock label={t('stats.labels.z_period')} value={p.zscore_period || 0} />
        <ParamBlock label={t('stats.labels.bars')} value={p.bar || p.bar_count || 0} />
        
        <ParamBlock label={t('stats.labels.margin')} value={`$${p.margin || 0}`} color="text-blue-400" />
        <ParamBlock label={t('stats.labels.leverage')} value={`${p.leverage || 0}x`} color="text-amber-400" />
        <ParamBlock label={t('stats.labels.comm')} value={displayComm} />

        <ParamBlock label={t('stats.labels.z_exit')} value={p.exit || '0'} color="text-rose-400" />
        <div className="col-span-2 grid grid-cols-2 gap-2">
            <ParamBlock label={t('stats.labels.levels_plus')} value={p.level_entry_positive?.join(',') || '-'} color="text-emerald-400" />
            <ParamBlock label={t('stats.labels.levels_minus')} value={p.level_entry_negative?.join(',') || '-'} color="text-rose-400" />
        </div>
      </div>

      <div className="p-5 mt-auto bg-slate-900/20 border-t border-slate-700/50 relative z-10">
        {!isPreview ? (
            <button 
                onClick={() => onAdd(strategy)}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-black uppercase tracking-widest transition-all shadow-lg text-xs bg-blue-600 hover:bg-blue-500 text-white shadow-blue-600/20 active:scale-95"
            >
                <HiPlay className="w-5 h-5" />
                <span>{t('stats.open_btn')}</span>
            </button>
        ) : (
            <div className="text-center py-2 text-[10px] text-slate-500 uppercase font-black tracking-[0.2em]">{t('stats.preview_mode')}</div>
        )}
      </div>
    </div>
  );
};

const StatsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCatalog = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getRecommendedStrategies();
      setStrategies(Array.isArray(data) ? data : []);
    } catch (e) { 
      console.error("Catalog load error:", e);
      setError(e.status === 403 ? t('stats.errors.access_denied') : t('stats.errors.generic'));
    } finally { 
      setLoading(false); 
    }
  };

  useEffect(() => {
    fetchCatalog();
  }, []);

  const handleOpenSimulation = (strategy) => {
    navigate(`/strategies/${strategy.publicStrategyId}`);
  };

  return (
    <div className="space-y-8 pb-10">
      <div>
        <h1 className="text-3xl font-black text-white tracking-tight">{t('stats.title')}</h1>
        <p className="text-slate-400 text-sm mt-1">{t('stats.subtitle')}</p>
      </div>

      {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {[1,2,3].map(i => <div key={i} className="h-[480px] bg-slate-800 rounded-2xl animate-pulse border border-slate-700" />)}
          </div>
      ) : error ? (
          <div className="py-10 px-6 bg-rose-500/10 border border-rose-500/20 rounded-2xl flex items-center gap-4 text-rose-400">
             <HiExclamation className="w-8 h-8 shrink-0" />
             <div>
                <p className="font-bold">{t('stats.errors.title')}</p>
                <p className="text-sm opacity-80">{error}</p>
             </div>
          </div>
      ) : strategies.length === 0 ? (
          <div className="py-20 text-center border-2 border-dashed border-slate-700 rounded-3xl bg-slate-800/30">
            <p className="text-slate-500 font-medium italic">{t('stats.errors.empty')}</p>
          </div>
      ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 items-stretch">
            {strategies.map((s) => (
              <StrategyDisplayCard 
                key={s.publicStrategyId} 
                strategy={s} 
                onAdd={handleOpenSimulation} 
              />
            ))}
          </div>
      )}
    </div>
  );
};

export default StatsPage;